import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Fab, Stack, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'

import { DiagnosticRTCServiceHandle, DiagnosticRTCServiceProps } from '../types'
import AudioGainControl from '../components/AudioGainControl'
import LiveAudioVisualizer, { LiveAudioVisualizerHandle } from '../components/LiveAudioVisualizer'

type OfflineServiceProps = DiagnosticRTCServiceProps & {
    children: React.ReactNode
}

function OfflineService(props: OfflineServiceProps, ref: React.Ref<DiagnosticRTCServiceHandle>) {
    const { children, customDevices, onDevicesChange, onStreamChange, type } = props
    const { t } = useTranslation()

    const [stream, setStream] = useState<MediaStream | null>(null)
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
    const [isScreenSharing, setIsScreenSharing] = useState(false)
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
    const [filterType, setFilterType] = useState<"bell" | "diaphragm" | "raw">('raw')

    const audioContextRef = useRef<AudioContext>(new AudioContext())
    const gainNodeRef = useRef<GainNode>(audioContextRef.current.createGain())
    const videoRef = useRef<HTMLVideoElement>(null)
    const audioRef = useRef<HTMLAudioElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const liveAudioVisualizerRef = useRef<LiveAudioVisualizerHandle>(null)

    // Get available devices
    const getDevices = useCallback(async () => {
        try {
            const deviceList = await navigator.mediaDevices.enumerateDevices()
            setDevices(deviceList)
            return deviceList
        } catch (error) {
            console.error('Error getting devices:', error)
            return []
        }
    }, [])

    // Initialize devices on mount
    useEffect(() => {
        getDevices()
    }, [getDevices])

    // Organize devices by type
    const { medicalCameras, videoMediaDevices, audioInputMediaDevices, stethoscopes } = useMemo(() => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput')
        const audioDevices = devices.filter(device => device.kind === 'audioinput')

        // Map custom devices to medical cameras and stethoscopes
        const medicalCameras = customDevices
            .filter(device => device.type === 'camera')
            .map(customDevice => ({
                id: customDevice._id,
                device: videoDevices.find(d => d.deviceId === customDevice.device.deviceId) || {
                    deviceId: customDevice.device.deviceId,
                    label: customDevice.customName,
                    kind: 'videoinput' as MediaDeviceKind
                },
                customName: customDevice.customName,
                iconURL: customDevice.iconURL,
                type: customDevice.type,
                resolutions: ['1920x1080', '1280x720', '640x480'] // Default resolutions for cameras
            }))

        const stethoscopes = customDevices
            .filter(device => device.type === 'stethoscope')
            .map(customDevice => ({
                id: customDevice._id,
                device: audioDevices.find(d => d.deviceId === customDevice.device.deviceId) || {
                    deviceId: customDevice.device.deviceId,
                    label: customDevice.customName,
                    kind: 'audioinput' as MediaDeviceKind
                },
                customName: customDevice.customName,
                iconURL: customDevice.iconURL,
                type: customDevice.type
            }))

        return {
            medicalCameras,
            stethoscopes,
            audioInputMediaDevices: audioDevices,
            videoMediaDevices: videoDevices.map(device => ({ ...device, resolutions: ['1920x1080', '1280x720', '640x480'] }))
        }
    }, [devices, customDevices])

    // Start media stream
    const startStream = useCallback(async (deviceId?: string, constraints?: MediaStreamConstraints) => {
        try {
            const defaultConstraints: MediaStreamConstraints = {
                video: type === 'video' ? {
                    deviceId: deviceId ? { exact: deviceId } : undefined,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } : false,
                audio: type === 'audio' || type === 'video' ? {
                    deviceId: deviceId && type === 'audio' ? { exact: deviceId } : undefined,
                    echoCancellation: true,
                    noiseSuppression: true
                } : false
            }

            const finalConstraints = constraints || defaultConstraints
            const newStream = await navigator.mediaDevices.getUserMedia(finalConstraints)

            setStream(newStream)
            setSelectedDeviceId(deviceId || null)

            return newStream
        } catch (error) {
            console.error('Error starting stream:', error)
            return null
        }
    }, [type])

    // Stop current stream
    const stopStream = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop())
            setStream(null)
        }
    }, [stream])

    // Capture screen functionality
    const captureScreen = useCallback((cb: (imageFile: File) => void) => {
        if (!videoRef.current || !canvasRef.current) return

        const video = videoRef.current
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')

        if (!ctx) return

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        ctx.drawImage(video, 0, 0)

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `medical-screenshot-${Date.now()}.png`, { type: 'image/png' })
                cb(file)
            }
        }, 'image/png')
    }, [])

    // Share screen functionality
    const shareScreen = useCallback(async (shouldShare: boolean) => {
        if (shouldShare && !isScreenSharing) {
            try {
                const screenStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true,
                    audio: true
                })
                setStream(screenStream)
                setIsScreenSharing(true)
            } catch (error) {
                console.error('Error sharing screen:', error)
            }
        } else if (!shouldShare && isScreenSharing) {
            stopStream()
            setIsScreenSharing(false)
            // Restart camera stream if needed
            if (selectedDeviceId) {
                startStream(selectedDeviceId)
            }
        }
    }, [isScreenSharing, selectedDeviceId, startStream, stopStream])

    // Select device
    const selectDevice = useCallback((deviceId: string) => {
        stopStream()
        startStream(deviceId)
    }, [startStream, stopStream])

    // Select resolution for video devices
    const selectResolution = useCallback((deviceId: string, resolution: string) => {
        if (type !== 'video') return

        // Parse resolution (e.g., "1920x1080" -> { width: 1920, height: 1080 })
        const [width, height] = resolution.split('x').map(Number)

        const constraints: MediaStreamConstraints = {
            video: {
                deviceId: { exact: deviceId },
                width: { ideal: width },
                height: { ideal: height }
            },
            audio: false
        }

        stopStream()
        startStream(deviceId, constraints)
    }, [type, startStream, stopStream])

    // Stop microphone (same as Peerjs)
    const stopMicro = useCallback(() => {
        liveAudioVisualizerRef.current?.stop()
        if (stream) {
            stream.getAudioTracks().forEach(track => track.stop())
            setStream(null)
        }
    }, [stream])

    // Stop video
    const stopVideo = useCallback(() => {
        if (stream) {
            stream.getVideoTracks().forEach(track => track.stop())
        }
    }, [stream])

    // Get current stream
    const getStream = useCallback(() => {
        return stream
    }, [stream])

    // Refresh connection (for offline mode, just restart stream)
    const refreshConnection = useCallback(() => {
        if (selectedDeviceId) {
            stopStream()
            setTimeout(() => {
                startStream(selectedDeviceId)
            }, 1000)
        }
    }, [selectedDeviceId, startStream, stopStream])

    // Render filter selector (same as Peerjs)
    const renderFilterSelector = useCallback(() => {
        return (
            <FormControl variant="outlined" fullWidth>
                <InputLabel>{t('component.group.stethoscope.filters.label')}</InputLabel>
                <Select
                    value={filterType}
                    onChange={(event: any) => setFilterType(event.target.value)}
                    label="Filter Type"
                >
                    <MenuItem value="raw">{t('component.group.stethoscope.filters.raw.label')}</MenuItem>
                    <MenuItem value="bell">{t('component.group.stethoscope.filters.bell.label')}</MenuItem>
                    <MenuItem value="diaphragm">{t('component.group.stethoscope.filters.diaphragm.label')}</MenuItem>
                </Select>
            </FormControl>
        )
    }, [filterType, t])

    // Render video component (exactly like Peerjs)
    const renderVideo = useCallback(
        () => (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <video autoPlay playsInline ref={videoRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}></video>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
        ),
        []
    )

    // Render audio component (exactly like Peerjs)
    const renderAudio = useCallback(() => <audio ref={audioRef} />, [])

    // Render audio visualizer (offline version without PeerConnectionIndicator)
    const renderAudioVisualizer = useCallback(() => {
        return (
            <>
                <Stack direction="row" spacing={1} p={1}>
                    <AudioGainControl
                        initialGain={1} // In offline mode, we're always the streamer
                        onGainChange={(gain) => {
                            if (!gainNodeRef.current) return
                            gainNodeRef.current.gain.value = gain
                        }}
                    />
                    <Fab color="primary" onClick={refreshConnection} size="small">
                        <RefreshRoundedIcon />
                    </Fab>
                </Stack>
                <LiveAudioVisualizer stream={stream ?? undefined} />
            </>
        )
    }, [refreshConnection, stream])

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        captureScreen,
        getStream,
        refreshConnection,
        renderAudio,
        renderAudioVisualizer,
        renderVideo,
        selectDevice,
        selectResolution,
        shareScreen,
        stopMicro,
        stopVideo,
    }))

    // Notify parent of devices change
    useEffect(() => {
        onDevicesChange({ medicalCameras, stethoscopes, audioInputMediaDevices, videoMediaDevices })
    }, [medicalCameras, stethoscopes, audioInputMediaDevices, videoMediaDevices, onDevicesChange])

    // Notify parent of stream change
    useEffect(() => {
        onStreamChange(stream)
    }, [stream, onStreamChange])

    // Auto-start stream on mount if devices are available
    useEffect(() => {
        if (devices.length > 0 && !stream) {
            const targetDevices = type === 'video' ?
                devices.filter(d => d.kind === 'videoinput') :
                devices.filter(d => d.kind === 'audioinput')

            if (targetDevices.length > 0) {
                startStream(targetDevices[0].deviceId)
            }
        }
    }, [devices, stream, type, startStream])

    // Audio context setup (same as Peerjs)
    useEffect(() => {
        const audioContext = audioContextRef.current
        // Set initial gain value
        gainNodeRef.current.gain.value = 1 // In offline mode, we're always the streamer
        return () => {
            audioContext.close()
        }
    }, [])

    // Audio stream handling (same as Peerjs)
    useEffect(() => {
        const audioContext = audioContextRef.current
        const audioElement = audioRef.current
        const gainNode = gainNodeRef.current

        if (!audioElement || !stream || type !== 'audio') return

        const source = audioContext.createMediaStreamSource(stream)

        gainNode.connect(audioContext.destination)
        source.connect(gainNode)

        audioElement.srcObject = stream
        audioElement.muted = false
        audioElement.onloadedmetadata = () => {
            audioElement.play()
        }

        return () => {
            gainNode.disconnect()
            source.disconnect()
            stream.getAudioTracks().forEach((track) => {
                track.stop()
            })
        }
    }, [stream, type])

    // Video stream handling (same as Peerjs)
    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement || !stream || type !== 'video') return
        videoElement.srcObject = stream
        videoElement.muted = true
        videoElement.onloadedmetadata = () => {
            videoElement.play()
        }

        return () => {
            stream.getVideoTracks().forEach((track) => {
                track.stop()
            })
        }
    }, [stream, type])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopStream()
        }
    }, [stopStream])

    return <>{children}</>
}

export default React.forwardRef(OfflineService) 